// @ts-strict-ignore
import {Component, computed, effect, inject, input, signal, viewChild} from "@angular/core";
import {
	ClinicalVignetteTimerService,
	ParticipantDeviceService,
	SimAssessmentService,
	SimMaterialDialogService,
	NotificationService,
	SimUtilityService,
	TypePredicates,
} from "@services";
import {AssessmentState, Participant, SuccessResponse, UserRecordingAssessment} from "@models";
import {AssessablePanelMode, AssessablePanelType} from "@common/components/assessment/utils";
import {Translations} from "@translations";
import {
	AssessmentAnswerPanelComponent,
	AssessmentAnswerPanelVariant,
} from "../assessable-panels/assessment-answer-panel/assessment-answer-panel.component";
import {TranslateService} from "@ngx-translate/core";
import {CdkPortal} from "@angular/cdk/portal";
import {zoomOutOnLeaveAnimation} from "angular-animations";

@Component({
	selector: "sim-participant-device-examiner-assessment-display",
	templateUrl: "./participant-device-examiner-assessment-display.component.html",
	styleUrl: "./participant-device-examiner-assessment-display.component.scss",
	animations: [zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class ParticipantDeviceExaminerAssessmentDisplayComponent {
	protected readonly T = Translations;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly AssessablePanelType = AssessablePanelType;
	protected readonly AssessmentState = AssessmentState;
	protected readonly AssessmentAnswerPanelVariant = AssessmentAnswerPanelVariant;

	public readonly participant = input.required<Participant>();
	public readonly clinicalVignetteText = input<string>();
	public readonly assessment = input.required<UserRecordingAssessment>();

	protected readonly participantDisplay = computed(() => SimUtilityService.mapParticipantToParticipantDisplay(this.participant()));
	public readonly assessmentPanel = viewChild.required(AssessmentAnswerPanelComponent);
	public readonly portalViewChild = viewChild(CdkPortal);

	readonly #simAssessmentService = inject(SimAssessmentService);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #translateService = inject(TranslateService);
	readonly #simNotificationService = inject(NotificationService);
	readonly #participantDeviceService = inject(ParticipantDeviceService);
	readonly #clinicalVignetteTimerService = inject(ClinicalVignetteTimerService, {optional: true});

	protected remainingTimeClinicalVignette =
		this.#clinicalVignetteTimerService?.remainingTimeClinicalVignette ?? signal<null>(null).asReadonly();

	constructor() {
		effect(() => {
			const viewChild = this.portalViewChild();
			if (!viewChild) {
				return;
			}
			this.#participantDeviceService.setPortal(viewChild);
		});
	}

	public onIndexGroupClicked(groupId: string) {
		this.assessmentPanel().expandGroup(groupId);
	}

	public onIndexItemClicked(eventData: {groupId: string; itemId: string}) {
		this.assessmentPanel().expandGroupAndItem(eventData.groupId, eventData.itemId);
	}

	public async abortAssessment(userRecordingAssessment: UserRecordingAssessment) {
		const {action, reason} = await this.#dialogService.openAbortAssessmentDialog();

		if (action !== "ok") {
			return;
		}

		const result = await this.#simAssessmentService
			.abortAssessment({
				userRecordingAssessmentId: userRecordingAssessment.id,
				reason: reason,
			})
			.catch<SuccessResponse>((_) => {
				this.#simNotificationService.notifyError(this.#translateService.instant(this.T.ASSESSMENT_PANEL_COULD_NOT_ABORT));
				return {success: false, reason: ""};
			});

		if (result.success) {
			this.#simNotificationService.notifySuccess(this.#translateService.instant(this.T.ASSESSMENT_PANE_SUCCESSFUL_ABORT));
		}
	}

	public async showClinicalVignette() {
		const examState = this.#participantDeviceService.examState();
		const clinicalVignetteDurationInSeconds =
			TypePredicates.isExamRunApplicationData(examState) && TypePredicates.isExamRunEncounterApplicationData(examState)
				? examState.clinicalVignetteDurationInSeconds
				: undefined;

		await this.#dialogService.openClinicalVignetteDialog({
			clinicalVignetteText: this.clinicalVignetteText(),
			phaseStartTime: this.#participantDeviceService.phaseStartTimeAdjusted,
			clinicalVignetteDurationInSeconds: clinicalVignetteDurationInSeconds,
		});
	}
}
